import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, formatIDR } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Printer,
  Barcode as BarcodeIcon,
  Tags,
  Merge,
  Power,
  Search,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import JsBarcode from "jsbarcode";
import { useAuth } from "@/contexts/AuthContext";

function BarcodeSvg({ value, height = 50 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      try {
        JsBarcode(ref.current, value, {
          format: "CODE128",
          displayValue: true,
          height,
          fontSize: 12,
          margin: 4,
        });
      } catch {}
    }
  }, [value, height]);
  return <svg ref={ref} />;
}

const empty = {
  name: "",
  sku: "",
  barcode: "",
  category_id: "",
  price: 0,
  cost: 0,
  stock: 0,
  min_stock: 5,
  unit: "pcs",
  outlet_id: "",
  image_url: "",
  active: true,
};

function categoryErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return "Gagal memproses kategori";
}

export default function Products() {
  const { hasFeature } = useAuth();
  const canPrintBarcode = hasFeature("barcode_print");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [printList, setPrintList] = useState([]);
  const [q, setQ] = useState("");

  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [productCategorySearch, setProductCategorySearch] = useState("");
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [mergeTargetId, setMergeTargetId] = useState("");

  const load = async () => {
    const [p, c, o] = await Promise.all([
      api.get("/products"),
      api.get("/categories?include_inactive=true"),
      api.get("/outlets"),
    ]);
    setProducts(p.data || []);
    setCategories(c.data || []);
    setOutlets(o.data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const sortedCategories = useMemo(
    () =>
      [...categories].sort((a, b) => {
        const activeOrder = Number(b.active !== false) - Number(a.active !== false);
        if (activeOrder !== 0) return activeOrder;
        return String(a.name || "").localeCompare(String(b.name || ""), "id");
      }),
    [categories],
  );

  const productCategoryOptions = useMemo(() => {
    const needle = productCategorySearch.trim().toLowerCase();
    return sortedCategories.filter((category) => {
      const selectable = category.active !== false || category.id === form.category_id;
      const matches =
        !needle ||
        String(category.name || "").toLowerCase().includes(needle) ||
        category.id === form.category_id;
      return selectable && matches;
    });
  }, [sortedCategories, productCategorySearch, form.category_id]);

  const managedCategories = useMemo(() => {
    const needle = categorySearch.trim().toLowerCase();
    if (!needle) return sortedCategories;
    return sortedCategories.filter((category) =>
      String(category.name || "").toLowerCase().includes(needle),
    );
  }, [sortedCategories, categorySearch]);

  const save = async () => {
    try {
      const body = {
        ...form,
        price: Number(form.price),
        cost: Number(form.cost),
        stock: Number(form.stock),
        min_stock: Math.max(0, Number(form.min_stock || 0)),
        category_id: form.category_id || null,
        outlet_id: form.outlet_id || null,
      };
      if (editId) await api.patch(`/products/${editId}`, body);
      else await api.post("/products", body);
      toast.success("Produk tersimpan");
      setOpen(false);
      setForm(empty);
      setEditId(null);
      setProductCategorySearch("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus produk?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Terhapus");
    load();
  };

  const resetCategoryEditor = () => {
    setCategoryName("");
    setEditingCategoryId(null);
  };

  const saveCategory = async (force = false) => {
    const name = categoryName.trim();
    if (!name) {
      toast.error("Nama kategori wajib diisi");
      return;
    }
    setCategorySaving(true);
    try {
      if (editingCategoryId) {
        await api.patch(`/categories/${editingCategoryId}`, { name, force });
        toast.success("Nama kategori diperbarui");
      } else {
        await api.post("/categories", { name, force });
        toast.success("Kategori ditambahkan");
      }
      resetCategoryEditor();
      await load();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "similar_category" && !force) {
        const names = (detail.suggestions || [])
          .map((item) => item.name)
          .filter(Boolean)
          .join(", ");
        const confirmed = window.confirm(
          `Ditemukan kategori yang mirip: ${names || "kategori lain"}.\n\nTetap simpan kategori "${name}"?`,
        );
        if (confirmed) {
          setCategorySaving(false);
          await saveCategory(true);
          return;
        }
      } else {
        toast.error(categoryErrorMessage(error));
      }
    } finally {
      setCategorySaving(false);
    }
  };

  const editCategory = (category) => {
    setEditingCategoryId(category.id);
    setCategoryName(category.name || "");
  };

  const toggleCategory = async (category) => {
    try {
      await api.patch(`/categories/${category.id}`, {
        active: category.active === false,
      });
      toast.success(
        category.active === false ? "Kategori diaktifkan" : "Kategori dinonaktifkan",
      );
      await load();
    } catch (error) {
      toast.error(categoryErrorMessage(error));
    }
  };

  const deleteCategory = async (category) => {
    if (
      !window.confirm(
        `Hapus kategori "${category.name}"? Tindakan ini hanya dapat dilakukan jika kategori tidak digunakan produk.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/categories/${category.id}`);
      toast.success("Kategori dihapus");
      if (editingCategoryId === category.id) resetCategoryEditor();
      await load();
    } catch (error) {
      toast.error(categoryErrorMessage(error));
    }
  };

  const beginMerge = (category) => {
    setMergeSourceId(category.id);
    setMergeTargetId("");
  };

  const mergeCategory = async () => {
    if (!mergeSourceId || !mergeTargetId) {
      toast.error("Pilih kategori tujuan");
      return;
    }
    const source = categories.find((item) => item.id === mergeSourceId);
    const target = categories.find((item) => item.id === mergeTargetId);
    if (
      !window.confirm(
        `Pindahkan seluruh produk dari "${source?.name}" ke "${target?.name}"?\n\nKategori asal akan dinonaktifkan.`,
      )
    ) {
      return;
    }
    try {
      const response = await api.post(`/categories/${mergeSourceId}/merge`, {
        target_category_id: mergeTargetId,
      });
      toast.success(
        `${response.data?.products_moved || 0} produk berhasil dipindahkan`,
      );
      setMergeSourceId("");
      setMergeTargetId("");
      await load();
    } catch (error) {
      toast.error(categoryErrorMessage(error));
    }
  };

  const printBarcodes = () => {
    if (!canPrintBarcode) {
      toast.error("Fitur cetak barcode tidak tersedia pada paket saat ini");
      return;
    }
    if (printList.length === 0) {
      toast.error("Pilih produk dulu");
      return;
    }
    const win = window.open("", "_blank", "width=800,height=600");
    win.document.write(
      `<html><head><title>Cetak Barcode</title><style>body{font-family:sans-serif;padding:16px;}.label{display:inline-block;border:1px solid #ddd;padding:8px;margin:4px;text-align:center;width:200px;}.name{font-weight:bold;font-size:11px;margin-bottom:4px;}.price{font-size:13px;font-weight:bold;margin-top:4px;}</style></head><body>`,
    );
    printList.forEach((id) => {
      const p = products.find((x) => x.id === id);
      if (!p) return;
      win.document.write(
        `<div class="label"><div class="name">${p.name}</div><svg id="bc-${p.id}"></svg><div class="price">${formatIDR(p.price)}</div></div>`,
      );
    });
    win.document.write(
      `<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script><script>window.onload=function(){`,
    );
    printList.forEach((id) => {
      const p = products.find((x) => x.id === id);
      if (!p) return;
      win.document.write(
        `try{JsBarcode("#bc-${p.id}","${p.barcode || p.sku || p.id}",{format:"CODE128",height:50,fontSize:12});}catch(e){}`,
      );
    });
    win.document.write(
      `setTimeout(function(){window.print();},300);}<\/script></body></html>`,
    );
    win.document.close();
  };

  const filtered = products.filter(
    (p) =>
      !q ||
      p.name?.toLowerCase().includes(q.toLowerCase()) ||
      p.sku?.toLowerCase().includes(q.toLowerCase()),
  );

  const mergeSource = categories.find((category) => category.id === mergeSourceId);

  return (
    <div className="p-6 lg:p-10">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">KATALOG</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">
            Produk & Barcode
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setCategoryManagerOpen(true)}
            data-testid="prod-manage-category-btn"
            className="px-4 py-2.5 text-sm border border-border hover:bg-zinc-100 inline-flex items-center gap-1.5"
          >
            <Tags className="h-4 w-4" />
            Kelola Kategori
          </button>
          {canPrintBarcode && (
            <button
              onClick={printBarcodes}
              data-testid="prod-print-barcode-btn"
              className="px-4 py-2.5 text-sm border border-zinc-900 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1"
            >
              <Printer className="h-3.5 w-3.5" />
              Cetak {printList.length > 0 ? `(${printList.length})` : ""}
            </button>
          )}
          <button
            onClick={() => {
              setForm(empty);
              setEditId(null);
              setProductCategorySearch("");
              setOpen(true);
            }}
            data-testid="prod-add-btn"
            className="px-4 py-2.5 text-sm bg-[#002FA7] text-white font-semibold inline-flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            Produk Baru
          </button>
        </div>
      </div>

      <div className="mb-4">
        <input
          data-testid="prod-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari produk…"
          className="w-full md:w-80 border border-border px-4 py-2.5 text-sm"
        />
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border">
            <tr>
              {canPrintBarcode && <th className="text-left p-3 w-8"></th>}
              <th className="text-left p-3 overline">Nama</th>
              <th className="text-left p-3 overline">SKU / Barcode</th>
              <th className="text-left p-3 overline">Kategori</th>
              <th className="text-right p-3 overline">Harga</th>
              <th className="text-right p-3 overline">HPP</th>
              <th className="text-right p-3 overline">Stok / Minimum</th>
              <th className="text-right p-3 overline">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={canPrintBarcode ? 8 : 7}
                  className="p-8 text-center text-zinc-500"
                >
                  Belum ada produk
                </td>
              </tr>
            )}
            {filtered.map((p) => {
              const category = categories.find((c) => c.id === p.category_id);
              return (
                <tr key={p.id} className="border-b border-border hover:bg-zinc-50">
                  {canPrintBarcode && (
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={printList.includes(p.id)}
                        onChange={(e) =>
                          setPrintList(
                            e.target.checked
                              ? [...printList, p.id]
                              : printList.filter((x) => x !== p.id),
                          )
                        }
                        data-testid={`prod-checkbox-${p.id}`}
                      />
                    </td>
                  )}
                  <td className="p-3 font-semibold">{p.name}</td>
                  <td className="p-3 font-mono text-xs">{p.barcode || p.sku}</td>
                  <td className="p-3 text-zinc-500">
                    <span>{category?.name || p.category_name || "-"}</span>
                    {category?.active === false && (
                      <span className="ml-2 text-[10px] font-semibold text-amber-700">
                        NONAKTIF
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right tabular-nums">
                    {formatIDR(p.price)}
                  </td>
                  <td className="p-3 text-right tabular-nums text-zinc-500">
                    {formatIDR(p.cost)}
                  </td>
                  <td className="p-3 text-right tabular-nums">
                    <div
                      className={
                        Number(p.stock || 0) <= Number(p.min_stock ?? 5)
                          ? "font-semibold text-rose-700"
                          : "font-semibold"
                      }
                    >
                      {p.stock ?? 0} {p.unit || "pcs"}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                      min. {p.min_stock ?? 5}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => {
                        setForm({ ...empty, ...p });
                        setEditId(p.id);
                        setProductCategorySearch("");
                        setOpen(true);
                      }}
                      className="p-1.5 hover:bg-zinc-200"
                      data-testid={`prod-edit-${p.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => del(p.id)}
                      className="p-1.5 hover:bg-rose-100 text-rose-600"
                      data-testid={`prod-del-${p.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="prod-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit" : "Tambah"} Produk</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="overline">Nama</label>
              <input
                data-testid="prod-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1"
              />
            </div>
            <div>
              <label className="overline">SKU</label>
              <input
                data-testid="prod-form-sku"
                value={form.sku || ""}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1"
              />
            </div>
            <div>
              <label className="overline">Barcode</label>
              <input
                data-testid="prod-form-barcode"
                value={form.barcode || ""}
                onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1"
              />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2">
                <label className="overline">Kategori</label>
                <button
                  type="button"
                  onClick={() => setCategoryManagerOpen(true)}
                  className="text-[10px] font-semibold text-[#002FA7] hover:underline"
                >
                  Kelola
                </button>
              </div>
              <div className="relative mt-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
                <input
                  value={productCategorySearch}
                  onChange={(e) => setProductCategorySearch(e.target.value)}
                  placeholder="Cari kategori"
                  className="w-full border border-border py-2 pl-9 pr-3 text-sm"
                />
              </div>
              <select
                data-testid="prod-form-category"
                value={form.category_id || ""}
                onChange={(e) =>
                  setForm({ ...form, category_id: e.target.value })
                }
                className="w-full border border-border px-3 py-2 mt-1"
              >
                <option value="">Tanpa kategori</option>
                {productCategoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                    {category.active === false ? " (Nonaktif)" : ""}
                  </option>
                ))}
              </select>
              {productCategorySearch &&
                productCategoryOptions.length === 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setCategoryManagerOpen(true);
                      setCategoryName(productCategorySearch);
                    }}
                    className="mt-1 text-xs font-semibold text-[#002FA7] hover:underline"
                  >
                    Tambahkan “{productCategorySearch}” sebagai kategori
                  </button>
                )}
            </div>
            <div>
              <label className="overline">Outlet (opsional)</label>
              <select
                data-testid="prod-form-outlet"
                value={form.outlet_id || ""}
                onChange={(e) => setForm({ ...form, outlet_id: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1"
              >
                <option value="">Semua Outlet</option>
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline">Harga Jual</label>
              <input
                data-testid="prod-form-price"
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1 font-mono-num"
              />
            </div>
            <div>
              <label className="overline">HPP / Modal</label>
              <input
                data-testid="prod-form-cost"
                type="number"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1 font-mono-num"
              />
            </div>
            <div>
              <label className="overline">Stok</label>
              <input
                data-testid="prod-form-stock"
                type="number"
                min="0"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1 font-mono-num"
              />
            </div>
            <div>
              <label className="overline">Batas Stok Rendah</label>
              <input
                data-testid="prod-form-min-stock"
                type="number"
                min="0"
                value={form.min_stock ?? 5}
                onChange={(e) =>
                  setForm({ ...form, min_stock: e.target.value })
                }
                className="w-full border border-border px-3 py-2 mt-1 font-mono-num"
              />
            </div>
            <div>
              <label className="overline">Satuan</label>
              <input
                data-testid="prod-form-unit"
                value={form.unit || "pcs"}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1"
              />
            </div>
            <div className="col-span-2">
              <label className="overline">URL Gambar (opsional)</label>
              <input
                data-testid="prod-form-image"
                value={form.image_url || ""}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="w-full border border-border px-3 py-2 mt-1"
              />
            </div>
            {form.barcode && (
              <div className="col-span-2 flex items-center gap-3 bg-zinc-50 p-3 border border-border">
                <BarcodeIcon className="h-4 w-4" />
                <BarcodeSvg value={form.barcode} />
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setOpen(false)}
              className="flex-1 py-3 border border-border"
              data-testid="prod-cancel-btn"
            >
              Batal
            </button>
            <button
              onClick={save}
              className="flex-1 py-3 bg-[#002FA7] text-white font-semibold"
              data-testid="prod-save-btn"
            >
              Simpan
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={categoryManagerOpen} onOpenChange={setCategoryManagerOpen}>
        <DialogContent className="rounded-none max-w-3xl" data-testid="category-manager-dialog">
          <DialogHeader>
            <DialogTitle>Master Kategori Produk</DialogTitle>
          </DialogHeader>

          <div className="border border-border bg-zinc-50 p-4">
            <div className="mb-2 text-sm font-semibold">
              {editingCategoryId ? "Ubah nama kategori" : "Tambah kategori"}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                data-testid="category-name-input"
                value={categoryName}
                onChange={(event) => setCategoryName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") saveCategory(false);
                }}
                placeholder="Contoh: Minuman Dingin"
                maxLength={80}
                className="min-w-0 flex-1 border border-border bg-white px-3 py-2 text-sm"
              />
              {editingCategoryId && (
                <button
                  type="button"
                  onClick={resetCategoryEditor}
                  className="border border-border px-4 py-2 text-sm"
                >
                  Batal Edit
                </button>
              )}
              <button
                type="button"
                disabled={categorySaving}
                onClick={() => saveCategory(false)}
                className="bg-[#002FA7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {categorySaving
                  ? "Menyimpan..."
                  : editingCategoryId
                    ? "Simpan Perubahan"
                    : "Tambah Kategori"}
              </button>
            </div>
            <p className="mt-2 text-xs text-zinc-500">
              Sistem merapikan spasi, mencegah nama duplikat tanpa membedakan
              huruf besar/kecil, dan memperingatkan nama yang mirip.
            </p>
          </div>

          {mergeSourceId && (
            <div className="border border-amber-300 bg-amber-50 p-4">
              <div className="mb-2 flex items-center gap-2 font-semibold text-amber-900">
                <Merge className="h-4 w-4" />
                Gabungkan kategori “{mergeSource?.name}”
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <select
                  value={mergeTargetId}
                  onChange={(event) => setMergeTargetId(event.target.value)}
                  className="min-w-0 flex-1 border border-amber-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Pilih kategori tujuan aktif</option>
                  {sortedCategories
                    .filter(
                      (category) =>
                        category.active !== false &&
                        category.id !== mergeSourceId,
                    )
                    .map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    setMergeSourceId("");
                    setMergeTargetId("");
                  }}
                  className="border border-amber-300 px-4 py-2 text-sm"
                >
                  Batal
                </button>
                <button
                  type="button"
                  onClick={mergeCategory}
                  className="bg-amber-700 px-4 py-2 text-sm font-semibold text-white"
                >
                  Gabungkan Produk
                </button>
              </div>
            </div>
          )}

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
            <input
              value={categorySearch}
              onChange={(event) => setCategorySearch(event.target.value)}
              placeholder="Cari master kategori"
              className="w-full border border-border py-2 pl-9 pr-3 text-sm"
            />
          </div>

          <div className="max-h-[420px] overflow-auto border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-zinc-50">
                <tr className="border-b border-border">
                  <th className="p-3 text-left overline">Kategori</th>
                  <th className="p-3 text-right overline">Produk</th>
                  <th className="p-3 text-left overline">Status</th>
                  <th className="p-3 text-right overline">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {managedCategories.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-zinc-500">
                      Kategori tidak ditemukan
                    </td>
                  </tr>
                )}
                {managedCategories.map((category) => (
                  <tr key={category.id} className="border-b border-border">
                    <td className="p-3">
                      <div className="font-semibold">{category.name}</div>
                      {category.merged_into_id && (
                        <div className="text-[10px] text-zinc-500">
                          Sudah digabungkan
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      {category.product_count || 0}
                    </td>
                    <td className="p-3">
                      <span
                        className={
                          category.active !== false
                            ? "text-emerald-700"
                            : "text-zinc-500"
                        }
                      >
                        {category.active !== false ? "Aktif" : "Nonaktif"}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() => editCategory(category)}
                        title="Ubah nama"
                        className="p-1.5 hover:bg-zinc-100"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleCategory(category)}
                        title={
                          category.active !== false
                            ? "Nonaktifkan"
                            : "Aktifkan"
                        }
                        className="p-1.5 hover:bg-zinc-100"
                      >
                        <Power className="h-3.5 w-3.5" />
                      </button>
                      {category.active !== false &&
                        sortedCategories.filter(
                          (item) =>
                            item.active !== false && item.id !== category.id,
                        ).length > 0 && (
                          <button
                            type="button"
                            onClick={() => beginMerge(category)}
                            title="Gabungkan ke kategori lain"
                            className="p-1.5 text-amber-700 hover:bg-amber-50"
                          >
                            <Merge className="h-3.5 w-3.5" />
                          </button>
                        )}
                      <button
                        type="button"
                        onClick={() => deleteCategory(category)}
                        disabled={Number(category.product_count || 0) > 0}
                        title={
                          Number(category.product_count || 0) > 0
                            ? "Kategori masih digunakan produk"
                            : "Hapus kategori"
                        }
                        className="p-1.5 text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
